import { useState, useCallback, useRef, useEffect } from "react";

import Alert from "ui-components/Alert";
import Modal from "ui-components/Modal";
import Button from "ui-components/Button";

import { useDeleteEntity } from "api";
import Divider from "ui-components/Divider";

interface Props {
  entityName: string;
  baseUrl: string;
  entityId: string;
  size?: "small" | "medium" | "large";
  viewHelperText?: React.ReactNode;
  useHelperText?: React.ReactNode;
}

const DeleteEntity = ({ entityName, baseUrl, entityId, size }: Props) => {
  const [submitStatus, setSubmitStatus] = useState<
    "not-started" | "in-progress" | "completed"
  >("not-started");
  const [error, setError] = useState<string>();
  const [showModal, setShowModal] = useState(false);

  const deleteEntity = useDeleteEntity();
  const componentIsMounted = useRef(true);
  useEffect(() => {
    // each useEffect can return a cleanup function
    return () => {
      componentIsMounted.current = false;
    };
  }, []); // no extra deps => the cleanup function run this on component unmounts

  const reset = useCallback(() => {
    setShowModal(false);
    setError(undefined);
    setSubmitStatus("not-started");
  }, [setShowModal, setError, setSubmitStatus]);

  const onConfirm = useCallback(async () => {
    setSubmitStatus("in-progress");
    try {
      deleteEntity({ baseUrl, entityId });
      if (componentIsMounted.current) {
        setShowModal(false);
      }
      setTimeout(reset, 3000);
    } catch (e) {
      setError("Request Failed!");
    }
    if (componentIsMounted.current) {
      setSubmitStatus("completed");
    }
  }, [setShowModal, setError, setSubmitStatus]);

  return (
    <div>
      <Button onClick={() => setShowModal(true)} color="gray" size={size}>
        DELETE
      </Button>
      <Modal
        id={`delete-${entityName}`}
        ariaLabel={`delete-${entityName}`}
        show={showModal}
        handleClose={() => setShowModal(false)}
        title={
          <>
            Delete {entityName} <span className="italic">{entityId}</span>
          </>
        }
      >
        <>
          <div className="m-4 p-4">
            <p className="mt-4">
              Are you sure you want to delete this {entityName}? Note this is an{" "}
              <b>irreversible</b> operation. All insights, and underlying data
              are deleted.
            </p>
          </div>
          <Divider />
          {error && (
            <div className="mt-4">
              <Alert>{error}</Alert>
            </div>
          )}
          {submitStatus === "completed" && !error && (
            <div className="mt-4">
              <Alert variant="success">Successfully Deleted!!!</Alert>
            </div>
          )}
          <div className="flex justify-end mt-4">
            <Button
              onClick={onConfirm}
              loading={submitStatus === "in-progress"}
            >
              Delete
            </Button>
          </div>
        </>
      </Modal>
    </div>
  );
};

export default DeleteEntity;
